import { transformPropsDeep, escapeHTML } from 'containers/App/utils';
import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get } from 'lodash';
import {
  mapQuestionsAndGroupsAsNodes,
  duplicateQuestion as duplicateQuestionHelper,
} from 'containers/QuestionnairePage/utils';
import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
import { SURVEY_TYPE_PEEQ } from 'containers/Survey/constants';

const productNamingGenerator = ({ template, variables, surveyType }) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  const Survey = new TemplateSurveyState({
    // fetchedQuestionnaire: template.questionnaireTemplate,
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  // extract and prepare user variables
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const productNameSuggestions = get(
    find(variables, ['name', 'product_names']),
    'value',
    [],
  );
  const suggestNames = get(
    find(variables, ['name', 'suggest_own_names']),
    'value',
    false,
  );

  // update texts in the template
  // use simplest / less performant option
  // of doing stringify, text replace, parse
  const replaceProductDescription = (value) =>
    value.replace(
      /\{\{product_category\}\}/gi,
      escapeHTML(productCategory.text),
    );

  const questionnaireTemplate = transformPropsDeep(
    template.questionnaireTemplate,
    {
      prompt: replaceProductDescription,
      htmlPrompt: replaceProductDescription,
      body: replaceProductDescription,
    },
  );

  const { nodes: productNamingTemplateQuestions } =
    mapQuestionsAndGroupsAsNodes(questionnaireTemplate);

  /*
    Prepare Template questions
  */
  const answers = productNameSuggestions.map((body, index) => ({
    type: 0,
    body,
    refId: `answer-ref-${index}`,
  }));

  const nameListQuestion = duplicateQuestionHelper(
    Object.assign({}, productNamingTemplateQuestions[0], {
      answers,
      media: productCategory.media,
    }),
  );

  const groupQuestions = productNameSuggestions.map((productName) => {
    const replaceProductName = (value) =>
      value.replace(/\{\{product_name\}\}/gi, escapeHTML(productName));

    const updatedQuestions = transformPropsDeep(
      productNamingTemplateQuestions[1],
      {
        prompt: replaceProductName,
        htmlPrompt: replaceProductName,
        body: replaceProductName,
      },
    );
    updatedQuestions.questions[0].media = productCategory.media;
    return duplicateQuestionHelper(updatedQuestions);
  });

  const rankLikenessQuestion = duplicateQuestionHelper(
    Object.assign({}, productNamingTemplateQuestions[2], { answers: [] }),
  );

  // link the carryForward configuration
  rankLikenessQuestion.carryForward[0].questionRefId = nameListQuestion.refId;

  const suggestOwnQuestionChoice = duplicateQuestionHelper(
    Object.assign({}, productNamingTemplateQuestions[3]),
  );
  const suggestOwnQuestion = duplicateQuestionHelper(
    Object.assign({}, productNamingTemplateQuestions[4]),
  );

  /*
    Add questions to questionnaire
  */
  Survey.questionnaire.addQuestion(nameListQuestion);
  groupQuestions.forEach((groupQuestion) =>
    Survey.questionnaire.addQuestion(groupQuestion),
  );
  Survey.questionnaire.addQuestion(rankLikenessQuestion);

  if (suggestNames) {
    Survey.questionnaire.addQuestion(suggestOwnQuestionChoice);
    Survey.questionnaire.addQuestion(suggestOwnQuestion);
  }

  // second pass to enable Carry Forward
  Survey.questionnaire.updateQuestion(nameListQuestion);

  /*
    Handle logic on answers
  */
  nameListQuestion.answers.forEach((answer, answerIndex) => {
    Survey.questionnaire.linkAnswerToQuestion({
      question: nameListQuestion,
      answerRefId: answer.refId,
      targetQuestion: groupQuestions[answerIndex],
    });
  });

  /*
    Handle logic on groups
  */
  groupQuestions.forEach((groupQuestion, groupIndex) => {
    nameListQuestion.answers.forEach((answer, answerIndex) => {
      if (answerIndex > groupIndex) {
        Survey.questionnaire.linkAnswerToQuestion({
          question: groupQuestion,
          answerRefId: answer.refId,
          targetQuestion: groupQuestions[answerIndex],
          context: { questionId: nameListQuestion.refId },
        });
      }
    });

    // change the in any other case
    Survey.questionnaire.setQuestionDefaultOutcome(
      groupQuestion,
      rankLikenessQuestion,
    );
  });

  /* 
    Handle logic for the suggest names questions
  */
  if (suggestNames) {
    const yesAnswer = suggestOwnQuestionChoice.answers.find(
      ({ body }) => body === 'Yes',
    );
    Survey.questionnaire.linkAnswerToQuestion({
      question: suggestOwnQuestionChoice,
      answerRefId: yesAnswer.refId,
      targetQuestion: suggestOwnQuestion,
    });
    Survey.questionnaire.setQuestionDefaultOutcome(suggestOwnQuestionChoice, {
      refId: END_OF_SURVEY,
    });
  }

  return { state: Survey.state };
};

export default productNamingGenerator;
